import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { UploadCloud, Loader2, Trash2, RefreshCw, Search, CheckCircle2 } from 'lucide-react';
import RebateUploadModal from '../RebateUploadModal';

const enconOrange = '#ee7203';

/**
 * Settings > Pricing > Rebates. Shows the "Rebate Summary" reference derived
 * from an uploaded spreadsheet, grouped by Product Analysis Group (via the
 * Stock Database) and best-matched to an existing/likely Pricing supplier.
 * Editable per row (rebate % and supplier match) — see routes/pricing.py
 * `_auto_apply_rebates_to_categories` for how these auto-attach to matching
 * categories, including ones added later.
 */
const RebateRow = ({ group, onSaved, onDeleted }) => {
  const [pct, setPct] = useState(group.rebate_pct);
  const [supplierName, setSupplierName] = useState(group.matched_supplier_name || '');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  const savePct = async () => {
    if (parseFloat(pct) === group.rebate_pct) return;
    setSaving(true);
    try {
      await api.put(`${API}/pricing/rebates/${group.id}`, { rebate_pct: parseFloat(pct) || 0 });
      onSaved();
    } catch {
      toast.error('Failed to update rebate %');
    } finally {
      setSaving(false);
    }
  };

  const saveSupplier = async () => {
    if ((supplierName || '') === (group.matched_supplier_name || '')) return;
    setSaving(true);
    try {
      await api.put(`${API}/pricing/rebates/${group.id}`, { matched_supplier_name: supplierName });
      toast.success('Supplier match updated');
      onSaved();
    } catch {
      toast.error('Failed to update supplier match');
    } finally {
      setSaving(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const res = await api.post(`${API}/pricing/rebates/${group.id}/apply`);
      toast.success(res.data.applied_to_categories > 0
        ? `Applied to ${res.data.applied_to_categories} categor${res.data.applied_to_categories === 1 ? 'y' : 'ies'}`
        : 'No matching Pricing category found for this supplier yet');
    } catch {
      toast.error('Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the rebate reference for "${group.group_key}"?`)) return;
    try {
      await api.delete(`${API}/pricing/rebates/${group.id}`);
      onDeleted();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b hover:bg-orange-50/40 text-sm" data-testid={`rebate-row-${group.id}`}>
      <div className="col-span-4 min-w-0">
        <p className="font-medium text-gray-800 truncate" title={group.group_key}>{group.group_key}</p>
        <p className="text-xs text-gray-400 truncate" title={group.description}>{group.description} · {group.sample_size} code{group.sample_size === 1 ? '' : 's'}</p>
      </div>
      <div className="col-span-3">
        <Input
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
          onBlur={saveSupplier}
          placeholder="Unmatched — type a supplier"
          className={`h-8 text-sm ${!group.matched_supplier_name ? 'text-gray-400 italic' : ''} ${group.matched_supplier_id ? 'border-green-300' : ''}`}
          data-testid={`rebate-supplier-input-${group.id}`}
        />
        {supplierName && !group.matched_supplier_id && (
          <p className="text-[11px] text-amber-600 mt-0.5">Suggested — not yet a Pricing supplier</p>
        )}
      </div>
      <div className="col-span-2">
        <div className="relative">
          <Input
            type="number" step="0.01" value={pct}
            onChange={(e) => setPct(e.target.value)}
            onBlur={savePct}
            className="h-8 text-sm pr-6"
            data-testid={`rebate-pct-input-${group.id}`}
          />
          <span className="absolute right-2 top-1.5 text-xs text-gray-400">%</span>
        </div>
      </div>
      <div className="col-span-3 flex items-center justify-end gap-1">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
        <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={apply} disabled={applying} data-testid={`rebate-apply-btn-${group.id}`}>
          {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Apply
        </Button>
        <button onClick={remove} className="p-1.5 text-red-400 hover:bg-red-50 rounded" data-testid={`rebate-delete-btn-${group.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export const PricingRebatesTab = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pricing/rebates`);
      setGroups(res.data?.groups || []);
    } catch {
      toast.error('Failed to load rebate reference');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g =>
      (g.group_key || '').toLowerCase().includes(q) ||
      (g.matched_supplier_name || '').toLowerCase().includes(q) ||
      (g.description || '').toLowerCase().includes(q)
    );
  }, [groups, search]);

  const matchedCount = groups.filter(g => g.matched_supplier_name).length;
  const linkedCount = groups.filter(g => g.matched_supplier_id).length;

  return (
    <div data-testid="pricing-rebates-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-500">
            Reference rebate % per product group, derived from an uploaded Rebate Summary sheet — auto-attaches
            to matching Pricing categories now, and to any new supplier/group added later.
          </p>
          {groups.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {groups.length} groups · {matchedCount} matched to a supplier ({linkedCount} already in Pricing) · {groups.length - matchedCount} unmatched
            </p>
          )}
        </div>
        <Button onClick={() => setUploadOpen(true)} style={{ backgroundColor: enconOrange }} className="text-white" data-testid="rebate-upload-btn">
          <UploadCloud className="w-4 h-4 mr-1" /> Upload Rebate Summary
        </Button>
      </div>

      {groups.length > 0 && (
        <div className="relative mb-3 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <Input
            placeholder="Search group, supplier or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="rebate-search-input"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No rebate reference yet — upload a Rebate Summary sheet to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 border-b">
            <div className="col-span-4">Product Group</div>
            <div className="col-span-3">Matched Supplier</div>
            <div className="col-span-2">Rebate %</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {filtered.map(g => (
              <RebateRow key={g.id} group={g} onSaved={load} onDeleted={load} />
            ))}
            {filtered.length === 0 && <p className="text-center text-gray-400 py-8">No matches for "{search}"</p>}
          </div>
        </div>
      )}

      <RebateUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
    </div>
  );
};

export default PricingRebatesTab;
